import br from "@/styles/breakpoints.module.css";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { BadgeCheck, CircleHelp, TriangleAlert } from "lucide-react";
import { FlexColumn, FlexRowAlignCenter } from "../Flex";
import { type BannerEvent, EVENT_Details } from "./constants";

export enum EventBannerType {
    ERROR = "error",
    WARNING = "warning",
    CALLOUT = "callout",
    INFO = "info",
}

const EVENT_TYPE_PALLETTE = {
    [EventBannerType.ERROR]: {
        color: themeColors.errorPrimary,
        backgroundColor: themeColors.videoLight,
    },
    [EventBannerType.WARNING]: {
        color: themeColors.primary3,
        backgroundColor: themeColors.flashcardsLight,
    },
    [EventBannerType.CALLOUT]: {
        color: themeColors.primary3,
        backgroundColor: themeColors.flashcardsLight,
    },
    [EventBannerType.INFO]: {
        color: themeColors.primary3,
        backgroundColor: themeColors.flashcardsLight,
    },
};

type BannerProps = {
    type: EventBannerType;
    event: BannerEvent;
    rightComponent?: React.ReactNode;
    style?: React.CSSProperties;
    labelProps?: React.HTMLProps<HTMLSpanElement>;
    hideBannerTypeIcon?: boolean;
};

const ICON_MAP = {
    [EventBannerType.ERROR]: TriangleAlert,
    [EventBannerType.WARNING]: TriangleAlert,
    [EventBannerType.CALLOUT]: BadgeCheck,
    [EventBannerType.INFO]: CircleHelp,
};

const Banner = ({ type, event, rightComponent, labelProps, style, hideBannerTypeIcon }: BannerProps) => {
    const BannerIcon = ICON_MAP[type];

    return (
        <FlexRowAlignCenter
            className={br.mdDownColumn}
            style={{
                gap: spacing.XS,
                padding: "1rem 2rem",
                marginInline: spacing.SM,
                borderRadius: "2rem",
                marginTop: spacing.XS,
                marginBottom: spacing.XS,
                backgroundColor: EVENT_TYPE_PALLETTE[type].backgroundColor,
                justifyContent: rightComponent ? "space-between" : "flex-start",
                ...style,
            }}>
            <FlexRowAlignCenter
                style={{
                    gap: spacing.XS,
                    ...style,
                }}>
                {!hideBannerTypeIcon && (
                    <BannerIcon
                        size={iconSizes.LG}
                        fill={EVENT_TYPE_PALLETTE[type].color}
                        color={EVENT_TYPE_PALLETTE[type].backgroundColor}
                        style={{ flexShrink: 0 }}
                    />
                )}
                <FlexColumn>
                    <span
                        className="secondaryTextBold1"
                        {...labelProps}
                        data-testid="auto-id-31c993f1-ceef-4e38-b68b-d0bb8fb2d234">
                        {EVENT_Details[event].label}
                    </span>
                    {EVENT_Details[event].instruction && (
                        <span
                            className="secondaryText1"
                            data-testid="auto-id-3bdea4a5-d240-4e1e-99be-4ba87b7b5b33">{EVENT_Details[event].instruction}</span>
                    )}
                </FlexColumn>
            </FlexRowAlignCenter>
            {rightComponent}
        </FlexRowAlignCenter>
    );
};

export default Banner;
